import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Sparkles,
  Zap,
  ShieldCheck,
  Clock3,
  CheckCircle2,
  Linkedin,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { fadeUp } from './shared';

/**
 * MidScrollCTA — high-conversion banner sandwiched between two sections.
 *
 * Two visual variants:
 *   variant="score"   — dark, premium, score-led (placed after FeatureShowcase)
 *   variant="trial"   — light, friendly, trial-led (placed after PersonasSection)
 *
 * Both are full-bleed cards with strong copy, primary CTA, microcopy, and a
 * small trust signal (stacked avatars or 5-star rating).
 *
 * Primary CTA now scrolls to the on-page `#calculator` or `#pricing` section
 * (never navigates away). The `onPrimaryCta` prop is kept for backward compat
 * but is no longer used.
 */

const STACKED_AVATARS = [
  { src: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=2.5&w=160&h=160&q=80', fb: 'MS' },
  { src: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=facearea&facepad=2.5&w=160&h=160&q=80', fb: 'KV' },
  { src: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=facearea&facepad=2.5&w=160&h=160&q=80', fb: 'AR' },
  { src: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=facearea&facepad=2.5&w=160&h=160&q=80', fb: 'RK' },
];

// eslint-disable-next-line no-unused-vars
export const MidScrollCTA = ({ variant = 'score', onPrimaryCta }) => {
  const isScore = variant === 'score';

  return (
    <section
      className="relative border-t border-border/60"
      aria-label="Get started"
      data-testid={`mid-cta-${variant}`}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <motion.div {...fadeUp}>
          <Card
            className={
              isScore
                ? 'relative rounded-[32px] border border-foreground/10 bg-foreground text-background overflow-hidden'
                : 'relative rounded-[32px] border border-primary/20 bg-card overflow-hidden'
            }
          >
            {/* decorative gradient */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background: isScore
                  ? 'radial-gradient(680px circle at 85% 18%, hsl(212 92% 52% / 0.45), transparent 55%), radial-gradient(520px circle at 8% 90%, hsl(205 85% 55% / 0.25), transparent 60%)'
                  : 'radial-gradient(620px circle at 80% 15%, hsl(212 92% 70% / 0.20), transparent 55%), radial-gradient(500px circle at 10% 90%, hsl(205 85% 60% / 0.12), transparent 60%)',
              }}
            />
            <CardContent className="relative p-8 sm:p-12 lg:p-16">
              <div className="grid items-center gap-10 lg:gap-14 lg:grid-cols-[1.15fr_0.85fr]">
                {/* Copy column */}
                <div>
                  <span
                    className={
                      isScore
                        ? 'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-mono-edge font-medium tracking-[0.22em] uppercase bg-background/15 text-background border border-background/20'
                        : 'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-mono-edge font-medium tracking-[0.22em] uppercase bg-[hsl(var(--ep-accent-soft,212_100%_96%))] text-primary border border-primary/15'
                    }
                  >
                    {isScore ? (
                      <>
                        <Zap size={12} aria-hidden="true" /> Score preview · 10 seconds
                      </>
                    ) : (
                      <>
                        <Sparkles size={12} aria-hidden="true" /> Executive presence, quietly built
                      </>
                    )}
                  </span>
                  <h2
                    className={
                      isScore
                        ? 'mt-5 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-[-0.04em] leading-[1.05] text-background'
                        : 'mt-5 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-[-0.04em] leading-[1.05] text-foreground'
                    }
                  >
                    {isScore ? (
                      <>
                        Stop guessing what to post.{' '}
                        <span className="text-[hsl(212_92%_75%)]">
                          Get your score in two minutes.
                        </span>
                      </>
                    ) : (
                      <>
                        Sound like a sharper you.{' '}
                        <span className="text-primary">
                          Not another AI account.
                        </span>
                      </>
                    )}
                  </h2>
                  <p
                    className={
                      isScore
                        ? 'mt-4 text-sm sm:text-base text-background/75 leading-relaxed max-w-xl'
                        : 'mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl'
                    }
                  >
                    {isScore
                      ? 'Upload your LinkedIn PDF — we assess it against Blayzrr\u2019s Profile Intelligence Framework, surface the one thing to fix first, and hand you a content plan you\u2019d actually post.'
                      : 'Get the full audit, the fix-first plan, and three voice-matched drafts on day one with the Profile Success Plan. Upgrade to the monthly Thought Leadership plan only if you want the weekly rhythm.'}
                  </p>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Button
                      onClick={() =>
                        document
                          .getElementById(isScore ? 'calculator' : 'pricing')
                          ?.scrollIntoView({ behavior: 'smooth' })
                      }
                      data-testid={`mid-cta-${variant}-primary`}
                      size="lg"
                      className={
                        isScore
                          ? 'h-12 px-6 rounded-full text-[15px] bg-background text-foreground hover:bg-background/90 shadow-md'
                          : 'h-12 px-6 rounded-full text-[15px] shadow-md'
                      }
                    >
                      {isScore ? 'Check my LinkedIn score' : 'See pricing'}
                      <ArrowRight size={15} className="ml-1.5" aria-hidden="true" />
                    </Button>
                  </div>
                  {/* Trust microcopy */}
                  <ul
                    className={
                      isScore
                        ? 'mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-background/70'
                        : 'mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted-foreground'
                    }
                  >
                    <li className="inline-flex items-center gap-1.5">
                      <Clock3 size={12} aria-hidden="true" /> 10 seconds score preview
                    </li>
                    <li className="inline-flex items-center gap-1.5">
                      <ShieldCheck size={12} aria-hidden="true" /> No Auto-pay
                    </li>
                    <li className="inline-flex items-center gap-1.5">
                      <CheckCircle2 size={12} aria-hidden="true" /> GDPR &amp; UK PECR compliant
                    </li>
                  </ul>
                </div>

                {/* Visual / proof column */}
                <div>
                  {isScore ? <ScoreProof /> : <TrialProof />}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Proof modules ---------------- */

const ScoreProof = () => (
  <div className="rounded-2xl border border-background/15 bg-background/8 p-5 sm:p-6 backdrop-blur-sm">
    <div className="flex items-center gap-2 text-[10px] font-mono-edge tracking-[0.22em] uppercase text-background/70">
      <Linkedin size={12} aria-hidden="true" /> Live preview
    </div>
    <div className="mt-3 flex items-end gap-3">
      <span className="font-display font-semibold text-6xl tracking-[-0.05em] tabular-nums text-background">
        82
      </span>
      <span className="pb-3 text-sm text-background/70">/ 100</span>
    </div>
    <div className="mt-2 h-1 rounded bg-background/15 overflow-hidden">
      <div className="h-full bg-[hsl(212_92%_75%)]" style={{ width: '82%' }} />
    </div>
    <div className="mt-5 grid grid-cols-3 gap-2">
      {[
        ['Discover.', 72],
        ['Trust', 84],
        ['Cadence', 78],
      ].map(([k, v]) => (
        <div
          key={k}
          className="rounded-lg border border-background/15 bg-background/5 p-2.5"
        >
          <p className="text-[9px] font-mono-edge tracking-[0.18em] uppercase text-background/60">
            {k}
          </p>
          <p className="mt-1 text-base font-medium tabular-nums text-background">{v}</p>
        </div>
      ))}
    </div>
    <p className="mt-4 text-[11px] text-background/65 italic">
      “Headline missing target role.” — Fix this first.
    </p>
  </div>
);

const TrialProof = () => (
  <div className="rounded-2xl border border-border/70 bg-background/70 backdrop-blur-sm p-5 sm:p-6">
    <div className="flex items-center gap-3">
      <div className="flex -space-x-2.5" aria-hidden="true">
        {STACKED_AVATARS.map((a, i) => (
          <Avatar key={i} className="h-9 w-9 ring-2 ring-card shadow-sm">
            <AvatarImage src={a.src} alt="" className="object-cover" loading="lazy" />
            <AvatarFallback className="bg-secondary text-foreground text-[10px] font-medium">
              {a.fb}
            </AvatarFallback>
          </Avatar>
        ))}
      </div>
      <div>
        <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
          Joined this week
        </p>
        <p className="text-sm font-semibold tracking-[-0.01em]">+148 professionals</p>
      </div>
    </div>
    <div className="mt-5 pt-5 border-t border-dashed border-border/70 space-y-3 text-sm">
      {[
        'Profile Success Plan \u2014 \u20b9199 one-time',
        '6 posts / month combined on Thought Leadership',
        'GDPR + UK PECR compliant',
      ].map((t) => (
        <div key={t} className="flex items-start gap-2.5">
          <CheckCircle2 size={15} className="mt-0.5 text-primary shrink-0" aria-hidden="true" />
          <span className="text-foreground/90">{t}</span>
        </div>
      ))}
    </div>
    <p className="mt-4 text-[11px] text-muted-foreground">
      Cancel anytime · Razorpay secure · No Auto-pay.
    </p>
  </div>
);
